import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { move, fileToUrl } from "../../lib/adminHelpers";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { UploadField } from "./UploadField";
import { RowControls, DraftBadge } from "./RowControls";

const slug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "").slice(0, 20);
const emptyTier = () => ({ _uid: crypto.randomUUID(), name: "", price: 0, delivery: "", features: [] });
const emptyAddon = () => ({ _uid: crypto.randomUUID(), name: "", price: 0 });
const emptyService = () => ({ key: "", title: "", blurb: "", icon: "Mic", image_url: "", tiers: [], addons: [] });

const ServiceFields = ({ f, set }) => {
  const updTier = (i, patch) => set({ ...f, tiers: f.tiers.map((t, x) => (x === i ? { ...t, ...patch } : t)) });
  const updAddon = (i, patch) => set({ ...f, addons: f.addons.map((a, x) => (x === i ? { ...a, ...patch } : a)) });
  return (
    <div className="space-y-3">
      <div><Label className="text-xs text-[#A1A1AA]">Title</Label>
        <Input value={f.title} onChange={(e) => set({ ...f, title: e.target.value })} data-testid="svc-title" className="bg-[#0B0C10] border-white/10 mt-1" /></div>
      <div><Label className="text-xs text-[#A1A1AA]">Blurb</Label>
        <Textarea value={f.blurb} onChange={(e) => set({ ...f, blurb: e.target.value })} rows={3} data-testid="svc-blurb" className="bg-[#0B0C10] border-white/10 mt-1" /></div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-xs text-[#A1A1AA]">Icon (lucide name)</Label>
          <Input value={f.icon} onChange={(e) => set({ ...f, icon: e.target.value })} placeholder="Mic" className="bg-[#0B0C10] border-white/10 mt-1" /></div>
        <UploadField label="Image" accept="image/*" testid="svc-image" current={f.image_url ? "Image set" : ""} onUploaded={(p) => set({ ...f, image_url: fileToUrl(p) })} />
      </div>

      <div className="flex items-center justify-between pt-1">
        <Label className="text-xs text-[#A1A1AA]">Pricing tiers ({f.tiers.length})</Label>
        <Button type="button" onClick={() => set({ ...f, tiers: [...f.tiers, emptyTier()] })} size="sm" variant="outline" className="h-7 border-white/15 bg-transparent text-white hover:bg-white/5 rounded-sm text-xs"><Plus size={13} className="mr-1" /> Tier</Button>
      </div>
      {f.tiers.map((t, i) => (
        <div key={t._uid || i} className="rounded border border-white/10 p-3 space-y-2 relative">
          <button onClick={() => set({ ...f, tiers: f.tiers.filter((_, x) => x !== i) })} className="absolute top-2 right-2 text-[#E04F5F] hover:opacity-70"><X size={14} /></button>
          <div className="grid grid-cols-3 gap-2 pr-6">
            <Input value={t.name} onChange={(e) => updTier(i, { name: e.target.value })} placeholder="Standard" className="bg-[#0B0C10] border-white/10 h-8 text-sm" />
            <Input type="number" value={t.price} onChange={(e) => updTier(i, { price: parseFloat(e.target.value) || 0 })} placeholder="Price" className="bg-[#0B0C10] border-white/10 h-8 text-sm" />
            <Input value={t.delivery} onChange={(e) => updTier(i, { delivery: e.target.value })} placeholder="3 days" className="bg-[#0B0C10] border-white/10 h-8 text-sm" />
          </div>
          <Textarea value={(t.features || []).join("\n")} onChange={(e) => updTier(i, { features: e.target.value.split("\n").filter(Boolean) })} rows={2} placeholder="One feature per line" className="bg-[#0B0C10] border-white/10 text-sm" />
        </div>
      ))}

      <div className="flex items-center justify-between pt-1">
        <Label className="text-xs text-[#A1A1AA]">Add-ons ({f.addons.length})</Label>
        <Button type="button" onClick={() => set({ ...f, addons: [...f.addons, emptyAddon()] })} size="sm" variant="outline" className="h-7 border-white/15 bg-transparent text-white hover:bg-white/5 rounded-sm text-xs"><Plus size={13} className="mr-1" /> Add-on</Button>
      </div>
      {f.addons.map((a, i) => (
        <div key={a._uid || i} className="flex items-center gap-2">
          <Input value={a.name} onChange={(e) => updAddon(i, { name: e.target.value })} placeholder="Add-on name" className="bg-[#0B0C10] border-white/10 h-8 text-sm" />
          <Input type="number" value={a.price} onChange={(e) => updAddon(i, { price: parseFloat(e.target.value) || 0 })} placeholder="$" className="bg-[#0B0C10] border-white/10 h-8 text-sm w-24" />
          <button onClick={() => set({ ...f, addons: f.addons.filter((_, x) => x !== i) })} className="text-[#E04F5F] hover:opacity-70"><X size={16} /></button>
        </div>
      ))}
    </div>
  );
};

export const ServicesManager = ({ bump, onChange }) => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyService());
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/services").then((r) => setItems(r.data)).catch(() => {});
  const done = () => { load(); onChange && onChange(); };
  useEffect(() => { load(); }, [bump]);

  const strip = (f) => ({
    ...f,
    tiers: (f.tiers || []).map(({ name, price, delivery, features }) => ({ name, price, delivery, features: features || [] })),
    addons: (f.addons || []).map(({ name, price }) => ({ name, price })),
  });
  const add = async () => {
    if (!form.title) { toast.error("Title required"); return; }
    const key = slug(form.title) + Math.random().toString(36).slice(2, 5);
    await api.put(`/admin/services/${key}`, { ...strip(form), key, status: "draft", order: items.length });
    toast.success("Added as Draft — click Go live to publish"); setForm(emptyService()); done();
  };
  const save = async () => {
    if (!editing.title) { toast.error("Title required"); return; }
    await api.put(`/admin/services/${editing.key}`, strip(editing));
    toast.success("Saved"); setEditing(null); done();
  };
  const publish = async (it) => { await api.put(`/admin/services/${it.key}`, { ...strip(it), status: "published" }); toast.success("Published"); done(); };
  const del = async (key) => { await api.delete(`/admin/services/${key}`); done(); };
  const toggleHide = async (it) => { await api.put(`/admin/services/${it.key}`, { ...strip(it), hidden: !it.hidden }); done(); };
  const reorder = async (i, dir) => { const next = move(items, i, dir); if (!next) return; await api.post("/admin/services/reorder", { ids: next.map((x) => x.key) }); load(); };
  const openEdit = (s) => setEditing({ ...emptyService(), ...s, tiers: (s.tiers || []).map((t) => ({ _uid: crypto.randomUUID(), ...t })), addons: (s.addons || []).map((a) => ({ _uid: crypto.randomUUID(), ...a })) });

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div className="rounded-lg border border-white/10 bg-[#12141A] p-6 h-fit">
        <h3 className="font-head text-lg mb-4 flex items-center gap-2"><Plus size={18} className="text-[#C8A97E]" /> Add Service</h3>
        <ServiceFields f={form} set={setForm} />
        <Button onClick={add} data-testid="svc-add" className="w-full mt-4 bg-[#C8A97E] text-[#0B0C10] hover:bg-[#B3956D] font-semibold rounded-sm">Add Service</Button>
      </div>

      <div className="space-y-2">
        {items.length === 0 && <p className="text-[#A1A1AA]">No services yet.</p>}
        {items.map((s, i) => (
          <div key={s.key} data-testid={`svc-row-${s.key}`} className={`flex items-center justify-between gap-3 rounded-lg border p-3 ${s.hidden ? "border-white/5 bg-[#0e0f14] opacity-60" : "border-white/10 bg-[#12141A]"}`}>
            <div className="min-w-0">
              <div className="font-medium truncate flex items-center gap-2">{s.title}<DraftBadge show={s.status === "draft"} />{s.hidden && <span className="text-[10px] uppercase text-[#E0A44F]">hidden</span>}</div>
              <div className="text-xs text-[#A1A1AA] truncate">{(s.tiers || []).length} tiers · {(s.addons || []).length} add-ons</div>
            </div>
            <RowControls testid={`svc-${s.key}`} hidden={s.hidden} isFirst={i === 0} isLast={i === items.length - 1}
              isDraft={s.status === "draft"} onPublish={() => publish(s)}
              onUp={() => reorder(i, -1)} onDown={() => reorder(i, 1)}
              onToggleHide={() => toggleHide(s)} onEdit={() => openEdit(s)} onDelete={() => del(s.key)} />
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#12141A] border-white/10 text-white max-h-[90vh] overflow-y-auto" data-testid="svc-edit-dialog">
          <DialogHeader><DialogTitle className="font-head">Edit Service</DialogTitle></DialogHeader>
          {editing && <ServiceFields f={editing} set={setEditing} />}
          <Button onClick={save} data-testid="svc-save" className="w-full mt-2 bg-[#C8A97E] text-[#0B0C10] hover:bg-[#B3956D] font-semibold rounded-sm">Save Changes</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};
